/**
 * Semantic audio buses.
 *
 * Scenes consume these and never raw FFT. Raw spectral data is available on
 * `AudioFrame.raw` for the Lab and diagnostics only, because binding visuals directly
 * to bins produces jitter rather than musicality.
 *
 * Every bus is normalized to 0..1 and is safe to read every frame.
 */

export const AUDIO_BUS_KEYS = [
  /** Overall perceived mass of the mix. Slow, stable, good for scale and population. */
  "body",
  /** Low-end weight. Good for gravity, compression, downward force. */
  "pressure",
  /** High-end shimmer. Good for glint, fine detail, surface sparkle. */
  "sparkle",
  /** Rate of spectral change. Good for turbulence and agitation. */
  "flux",
  /** Transient envelope. Spikes to 1 on a hit and decays. Apply as a bounded impulse. */
  "onset",
  /** Position inside the current beat, 0 at the downbeat rising to 1. Wraps. */
  "beatPhase",
  /** Multi-bar rise and fall. Good for slow blooms and camera drift. */
  "breath",
  /** Spectral brightness and strain. Good for colour temperature and stretch. */
  "tension",
  /** Likelihood that a phrase boundary just happened. Good for one-shot scene events. */
  "novelty",
] as const;

export type AudioBusKey = (typeof AUDIO_BUS_KEYS)[number];

export type AudioBuses = Readonly<Record<AudioBusKey, number>>;

export const SILENT_BUSES: AudioBuses = Object.freeze({
  body: 0,
  pressure: 0,
  sparkle: 0,
  flux: 0,
  onset: 0,
  beatPhase: 0,
  breath: 0,
  tension: 0,
  novelty: 0,
});

/** Diagnostic spectral snapshot. Lab surfaces only. Never bind a scene to this. */
export interface RawSpectralFrame {
  readonly bass: number;
  readonly mid: number;
  readonly treble: number;
  readonly rms: number;
  /** Spectral centroid in Hz. */
  readonly centroid: number;
  readonly flux: number;
}

export interface AudioFrame {
  /** Seconds since analysis start. Monotonic. */
  readonly tSeconds: number;
  readonly buses: AudioBuses;
  /** Estimated tempo, or 0 before enough beats have been observed. */
  readonly bpm: number;
  /**
   * Monotonic count of detected beats. This is the edge signal: compare against the
   * previous frame's value to fire a one-shot. Never threshold `onset` directly, because
   * a level test re-fires on every frame the envelope stays high.
   */
  readonly beatCount: number;
  /**
   * How far into a rise the music is, 0..1, and whether it broke open on this frame.
   *
   * Optional because a frame can be synthesised without any structure behind it. Read `build`
   * as a level and `drop` strictly as an edge.
   */
  readonly build?: number;
  readonly drop?: boolean;
  /**
   * How much this frame sounds like a new note, before anything smooths it.
   *
   * Not a bus: buses are envelopes for driving a scene, and an envelope cannot say when. A
   * chart is written from this, which is why it has to be the raw per-frame figure.
   */
  readonly noteFlux?: number;
  /** The two bands per bin, which is how a note can be told from a kick. */
  readonly melodyFlux?: number;
  readonly lowFlux?: number;
  /** Spectral centroid in Hz: roughly how high the moment sounds. */
  readonly centroid?: number;
  /** The same over the melodic band, which is what a lane should follow. */
  readonly melodyCentroid?: number;
  /**
   * How steady the beat looks, 0..1. Low means `bpm` and `beatPhase` are a guess.
   *
   * The detector has always known this and used to keep it to itself, so everything downstream
   * treated a tempo read off a rubato piano intro exactly as it treated one read off four bars
   * of a drum machine. Anything that leans on the grid should lean less as this falls: play
   * fewer notes rather than more when the music has not said what it is doing yet.
   */
  readonly beatConfidence?: number;
  /**
   * How much of each of the twelve pitch classes is sounding, summing to one.
   *
   * Only present on an offline analysis, and deliberately: live scenes have no use for it and
   * would pay twelve numbers a frame for nothing. What it is for is telling one passage from
   * another, which is a question that can only be asked once the whole track has been heard.
   */
  readonly chroma?: readonly number[];
  readonly raw?: RawSpectralFrame;
}

export const SILENT_FRAME: AudioFrame = Object.freeze({
  tSeconds: 0,
  buses: SILENT_BUSES,
  bpm: 0,
  beatCount: 0,
});

export function isAudioBusKey(value: string): value is AudioBusKey {
  return (AUDIO_BUS_KEYS as readonly string[]).includes(value);
}
